import { EventId, EventInviteeId, rsvpResponseLabels } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { EventInviteeDto } from "components/calendar/calendar-types";
import { formatInviteeName } from "components/calendar/invitee-copy";
import { trpc } from "components/rpc/trpc";
import { Checkbox } from "components/ui/checkbox";
import { InlineBanner } from "components/ui/inline-banner";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { TextArea } from "components/ui/textarea";
import React, { useCallback, useState } from "react";
import { attendanceSummary, initialAttendance, toggleAttendance } from "./event-outcome";

interface EventOutcomeEditorProps {
    eventId: EventId;
    invitees: EventInviteeDto[];
    currentOutcome: string | null;
    onSaved: () => Promise<unknown>;
    onClose: () => void;
}

export const EventOutcomeEditor: React.FC<EventOutcomeEditorProps> = props => {
    const { eventId, invitees, currentOutcome, onSaved, onClose } = props;
    const trpcClient = trpc.useTRPC();
    const [attended, setAttended] = useState<EventInviteeId[]>(() =>
        initialAttendance(invitees),
    );
    const [outcome, setOutcome] = useState(currentOutcome ?? "");

    const completeMutation = useMutation({
        ...trpcClient.event.complete.mutationOptions(),
        onSuccess: async () => {
            await onSaved();
            onClose();
        },
    });

    const handleToggle = useCallback(
        (inviteeId: EventInviteeId) =>
            setAttended(current => toggleAttendance(current, inviteeId)),
        [],
    );

    const handleSave = useCallback(() => {
        completeMutation.mutate({
            id: eventId,
            outcome: outcome.trim() === "" ? null : outcome.trim(),
            attendedInviteeIds: attended,
        });
    }, [completeMutation, eventId, outcome, attended]);

    return (
        <Panel
            isOpen={true}
            type={PanelType.medium}
            headerText="Record outcome"
            onDismiss={onClose}>
            <div className="space-y-4 p-4">
                <p className="text-sm text-gray-500">
                    Check everyone who came. Each guest who attended gets this event on
                    their record as an activity, so it counts toward your outreach.
                </p>
                {completeMutation.isError ? (
                    <InlineBanner type="error">
                        Could not record the outcome. Please try again.
                    </InlineBanner>
                ) : null}
                <div>
                    <h3 className="text-sm font-medium text-gray-700">Who attended</h3>
                    {invitees.length === 0 ? (
                        <p className="mt-1 text-sm text-gray-500">
                            No one was invited to this event, so there is no attendance to
                            record.
                        </p>
                    ) : (
                        <>
                            <p className="mt-1 text-xs text-gray-500">
                                {attendanceSummary(attended.length, invitees.length)}
                            </p>
                            <ul className="mt-2 divide-y divide-gray-100">
                                {invitees.map(invitee => (
                                    <li key={invitee.id} className="py-2">
                                        <Checkbox
                                            checked={attended.includes(invitee.id)}
                                            onChange={() => handleToggle(invitee.id)}
                                            label={
                                                <span className="text-sm text-gray-900">
                                                    {formatInviteeName(invitee.name)}
                                                    {invitee.rsvpResponse ? (
                                                        <span className="ml-1 text-xs text-gray-500">
                                                            RSVP:{" "}
                                                            {
                                                                rsvpResponseLabels[
                                                                    invitee.rsvpResponse
                                                                ]
                                                            }
                                                        </span>
                                                    ) : null}
                                                </span>
                                            }
                                        />
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
                <TextArea
                    label="How did it go?"
                    rows={4}
                    value={outcome}
                    placeholder="What happened, who to follow up with, anything worth remembering."
                    onChange={e => setOutcome(e.target.value)}
                />
                <div className="flex justify-end space-x-2">
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                    <PrimaryButton
                        onClick={handleSave}
                        disabled={completeMutation.isPending}>
                        Save outcome
                    </PrimaryButton>
                </div>
            </div>
        </Panel>
    );
};
